import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, TableBody, TableCell, TableHead, TableRow, Typography, Box, Button, TextField, Checkbox, Switch, FormControlLabel, Collapse } from '@mui/material';

export default function SystemStatusPage() {
  const [simCount, setSimCount] = useState(3);
  const [simScaling, setSimScaling] = useState(false);
  const [simScaleMsg, setSimScaleMsg] = useState('');

  // Simulator control (power + routing mode)
  const [simPowerOn, setSimPowerOn] = useState(true);
  const [simMode, setSimMode] = useState('internal'); // 'internal' | 'external'
  const [simMqttUrl, setSimMqttUrl] = useState('');
  const [simConfigLoading, setSimConfigLoading] = useState(false);

  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState('');
  // rows from /api/status (one row per container)
  const [services, setServices] = useState([]); // [{ key, service, displayName, containerName, state, running, startedAt, restartCount, cpuPercent, memPercent, healthOk, healthDetails }]
  const [openServiceKey, setOpenServiceKey] = useState(null);

  // System summary (derived from /api/status)
  const [systemCpuPct, setSystemCpuPct] = useState(null);
  const [systemMemPct, setSystemMemPct] = useState(null);
  const [messageRatePerMin, setMessageRatePerMin] = useState(null);

  // Track mqtt_received from ingestor over time to compute message rate
  const mqttHistoryRef = useRef([]); // [{ t: number, mqttReceived: number }]

  // Firmware E2E test (minimal suite)
  const [fwEnabled, setFwEnabled] = useState(false);
  const [fwRunning, setFwRunning] = useState(false);
  const [fwMsg, setFwMsg] = useState('Enable the test to see results.');
  const [fwResults, setFwResults] = useState([]); // [{ wfmc, status, details, ms }]
  const [compareBinaries, setCompareBinaries] = useState(false);

  // Settings E2E test (minimal suite)
  const [settingsEnabled, setSettingsEnabled] = useState(false);
  const [settingsRunning, setSettingsRunning] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('Enable the test to see results.');
  const [settingsResults, setSettingsResults] = useState([]); // [{ wfmc, status, version, frqM, details, ms }]

  // Message Persistence E2E test (Phase 2 UI, backend endpoints)
  const [msgEnabled, setMsgEnabled] = useState(false);
  const [msgRunning, setMsgRunning] = useState(false);
  const [msgMsg, setMsgMsg] = useState('Enable the test to see results.');
  const [msgResults, setMsgResults] = useState([]); // [{ wfmc, status, details, ms, snapshot, influxStatus, s3Status, influxResp, s3Resp, snapshotResp, attempts, lastCheckedMs }]
  const [openMsgWfmc, setOpenMsgWfmc] = useState(null);


  const pad3 = (n) => String(n).padStart(3, '0');

  const simulatorWfmcs = useMemo(() => {
    const n = Math.max(1, Math.min(10, Number(simCount)));
    if (!Number.isFinite(n) || Number.isNaN(n)) return [];
    return Array.from({ length: n }, (_, i) => `SIMULATOR${pad3(i + 1)}`);
  }, [simCount]);

  const exampleLastMessages = useMemo(() => {
    return simulatorWfmcs.reduce((acc, wfmc, idx) => {
      const now = new Date(Date.now() - idx * 2000);
      acc[wfmc] = {
        wfmc,
        dt: now.toISOString(),
        type: idx % 2 === 0 ? 'co2' : 'temp',
        val: idx % 2 === 0 ? String(390 + idx * 5) : String((25.0 + idx * 0.2).toFixed(1)),
        units: idx % 2 === 0 ? 'ppm' : 'celsius'
      };
      return acc;
    }, {});
  }, [simulatorWfmcs]);

  const loadServices = async () => {
    setServicesLoading(true);
    setServicesError('');

    // Load simulator config (power/mode/desiredCount) from status service
    try {
      setSimConfigLoading(true);
      const rc = await fetch('/api/simulator/config', { cache: 'no-store' });
      const cfg = await rc.json().catch(() => null);
      if (rc.ok && cfg && cfg.ok) {
        if (typeof cfg.powerOn === 'boolean') setSimPowerOn(cfg.powerOn);
        if (cfg.mode === 'internal' || cfg.mode === 'external') setSimMode(cfg.mode);
        if (typeof cfg.mqttUrl === 'string') setSimMqttUrl(cfg.mqttUrl);
        if (Number.isFinite(Number(cfg.desiredCount))) setSimCount(String(cfg.desiredCount));
      }
    } catch (e) {
      // ignore (UI should still work if endpoint missing)
    } finally {
      setSimConfigLoading(false);
    }

    try {
      // Use /api/status because it already aggregates: container info + health + stats
      const res = await fetch('/api/status', { cache: 'no-store' });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data || data.ok !== true || !Array.isArray(data.rows)) {
        const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
        setServicesError(`Failed to load status: ${msg}`);
        setServices([]);
        return;
      }

      const normalized = data.rows
        .filter((r) => r && (r.containerName || r.service))
        .map((r) => {
          const key = String(r.containerId || `${r.service || 'svc'}-${r.replicaIndex || 0}-${r.containerName || ''}`);
          const memUsage = Number(r?.stats?.mem_usage_bytes || 0);
          const memLimit = Number(r?.stats?.mem_limit_bytes || 0);
          const memPercent = memUsage > 0 && memLimit > 0 ? (memUsage / memLimit) * 100 : null;

          // Health details can be long; we'll show it only when the row is expanded.
          const healthOk = r?.health?.ok === true;
          const healthBody = r?.health?.body;
          const bodyMsg = healthBody && typeof healthBody === 'object' ? (healthBody.message || healthBody.status || null) : null;
          const healthDetails = {
            err: r?.health?.err ?? null,
            status: r?.health?.status ?? null,
            body: r?.health?.body ?? null,
            message: bodyMsg
          };

          return {
            key,
            service: String(r.service || ''),
            displayName: String(r.displayName || r.service || ''),
            containerName: String(r.containerName || ''),
            state: String(r.state || ''),
            running: !!r.running,
            startedAt: String(r.startedAt || ''),
            restartCount: Number.isFinite(Number(r.restartCount)) ? Number(r.restartCount) : 0,
            cpuPercent: Number.isFinite(Number(r?.stats?.cpu_percent)) ? Number(r.stats.cpu_percent) : null,
            memPercent,
            healthOk,
            healthDetails
          };
        });

      // --- Derive system-level summary metrics from rows ---
      const cpuSum = normalized.reduce((acc, r) => acc + (Number.isFinite(r.cpuPercent) ? r.cpuPercent : 0), 0);
      const memUsageSum = data.rows.reduce((acc, r) => acc + Number(r?.stats?.mem_usage_bytes || 0), 0);
      const memLimitSum = data.rows.reduce((acc, r) => acc + Number(r?.stats?.mem_limit_bytes || 0), 0);

      const cpuPct = Number.isFinite(cpuSum) ? Math.max(0, Math.min(999, cpuSum)) : null;
      const memPct = (memUsageSum > 0 && memLimitSum > 0)
        ? Math.max(0, Math.min(100, (memUsageSum / memLimitSum) * 100))
        : null;

      setSystemCpuPct(Number.isFinite(cpuPct) ? Number(cpuPct.toFixed(1)) : null);
      setSystemMemPct(Number.isFinite(memPct) ? Number(memPct.toFixed(1)) : null);

      // Message rate: use ingestor health.body.stats.mqtt_received
      const ingestorRow = data.rows.find(r => r && r.service === 'ingestor');
      const mqttReceived = Number(ingestorRow?.health?.body?.stats?.mqtt_received);
      if (Number.isFinite(mqttReceived)) {
        const now = Date.now();
        const windowMs = 3 * 60 * 1000;
        const hist = mqttHistoryRef.current;
        hist.push({ t: now, mqttReceived });
        // Keep only last 3 minutes
        mqttHistoryRef.current = hist.filter(p => (now - p.t) <= windowMs);

        const h = mqttHistoryRef.current;
        if (h.length >= 2) {
          const first = h[0];
          const last = h[h.length - 1];
          const dtSec = (last.t - first.t) / 1000;
          const dCount = last.mqttReceived - first.mqttReceived;
          if (dtSec > 0 && dCount >= 0) {
            const perMin = (dCount / dtSec) * 60;
            setMessageRatePerMin(Math.max(0, perMin));
          }
        }
      }

      setServices(normalized);
    } catch (e) {
      setServicesError(`Failed to load status: ${e?.message || String(e)}`);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
    const id = setInterval(() => {
      loadServices();
    }, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  const setSimulatorPower = async (on) => {
    setSimScaling(true);
    setSimScaleMsg(on ? 'Starting simulators…' : 'Stopping simulators…');
    try {
      const res = await fetch('/api/simulator/power', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ on: !!on })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok !== true) {
        const err = (data && (data.error || data.message)) || `HTTP ${res.status}`;
        setSimScaleMsg(`Failed: ${err}`);
        return;
      }
      setSimPowerOn(!!data.powerOn);
      // Be tolerant to backend field naming (mode vs mqttMode)
      const backendMode = data && (data.mode || data.mqttMode);
      if (backendMode === 'internal' || backendMode === 'external') setSimMode(backendMode);
      if (typeof data.desiredCount !== 'undefined') setSimCount(String(data.desiredCount));
      setSimScaleMsg(data.message || (data.powerOn ? 'Simulators started' : 'Simulators stopped'));
      loadServices();
    } catch (e) {
      setSimScaleMsg(`Failed: ${e?.message || String(e)}`);
    } finally {
      setSimScaling(false);
    }
  };

  const setSimulatorMode = async (mode) => {
    const m = mode === 'external' ? 'external' : 'internal';
    setSimScaling(true);
    setSimScaleMsg(`Switching simulator routing to ${m}…`);
    try {
      const res = await fetch('/api/simulator/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: m })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok !== true) {
        const err = (data && (data.error || data.message)) || `HTTP ${res.status}`;
        setSimScaleMsg(`Failed: ${err}`);
        return;
      }
      // Be tolerant to backend field naming (mode vs mqttMode)
      const backendMode = (data && (data.mode || data.mqttMode)) || m;
      setSimMode(backendMode === 'external' ? 'external' : 'internal');
      if (typeof data.mqttUrl === 'string') setSimMqttUrl(data.mqttUrl);
      if (typeof data.desiredCount !== 'undefined') setSimCount(String(data.desiredCount));
      setSimPowerOn(!!data.powerOn);
      setSimScaleMsg(`Simulator routing: ${backendMode}.`);
      loadServices();
    } catch (e) {
      setSimScaleMsg(`Failed: ${e?.message || String(e)}`);
    } finally {
      setSimScaling(false);
    }
  };

const scaleSimulator = async () => {
    const count = Math.max(0, Math.min(10, Number(simCount)));
    if (!Number.isFinite(count) || Number.isNaN(count)) {
      setSimScaleMsg('Please enter a valid number (0-10).');
      return;
    }

    setSimScaling(true);
    setSimScaleMsg('Scaling...');
    try {
      const res = await fetch('/api/simulator/scale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count })
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const err = (data && (data.error || data.message)) || `HTTP ${res.status}`;
        setSimScaleMsg(`Failed to scale: ${err}`);
        return;
      }

      const runningCount = data && (data.runningCount ?? data.count ?? data.instances);
      setSimScaleMsg(`Scale requested: ${count}. ${runningCount != null ? `Running: ${runningCount}` : ''}`.trim());
    } catch (e) {
      setSimScaleMsg(`Failed to scale: ${e?.message || String(e)}`);
    } finally {
      setSimScaling(false);
    }
  };

  // Settings E2E test (minimal)
  const runSettingsTest = async () => {
    if (!settingsEnabled) {
      setSettingsMsg('Enable the test to run it.');
      return;
    }

    if (!simulatorWfmcs.length) {
      setSettingsMsg('No simulator WFMCs to test.');
      return;
    }

    setSettingsRunning(true);
    setSettingsMsg('Running settings checks…');

    const runOne = async (wfmc) => {
      const started = Date.now();
      try {
        const res = await fetch(`/api/test/settings?wfmc=${encodeURIComponent(wfmc)}`, { cache: 'no-store' });
        const ms = Date.now() - started;

        let data = null;
        let text = '';
        try {
          data = await res.clone().json();
        } catch {
          text = await res.text().catch(() => '');
        }

        if (!res.ok || !data || data.ok === false) {
          const details = (data && (data.error || (Array.isArray(data.errors) ? data.errors.join('; ') : null))) || '';
          const snippet = text ? String(text).replace(/\s+/g, ' ').slice(0, 160) : '';
          return { wfmc, status: 'ERROR', details: `HTTP ${res.status}${details ? `: ${details}` : ''}${snippet ? `: ${snippet}` : ''}`, ms };
        }

        // /api/test/settings returns a normalized shape
        const version = data?.settings?.version;
        const frqM = Number(data?.settings?.frqM);

        if (typeof version !== 'string' || !Number.isFinite(frqM)) {
          return {
            wfmc,
            status: 'ERROR',
            details: `Invalid response shape: ${JSON.stringify(data)}`,
            ms
          };
        }

        return {
          wfmc,
          status: 'PASS',
          version,
          frqM,
          details: 'OK',
          ms
        };
      } catch (e) {
        const ms = Date.now() - started;
        return { wfmc, status: 'ERROR', details: `Network error: ${e?.message || String(e)}`, ms };
      }
    };

    try {
      const results = await Promise.all(simulatorWfmcs.map(runOne));
      setSettingsResults(results);
      const okCount = results.filter(r => r.status === 'PASS').length;
      setSettingsMsg(`Done. ${okCount}/${results.length} passed.`);
    } finally {
      setSettingsRunning(false);
    }
  };

  const runMessageTest = async () => {
    if (!msgEnabled) {
      setMsgMsg('Enable the test to run it.');
      return;
    }
    if (!simulatorWfmcs.length) {
      setMsgMsg('No simulator WFMCs to test.');
      return;
    }

    const startedAt = Date.now();
    setMsgRunning(true);
    setOpenMsgWfmc(null);
    setMsgMsg('Capturing snapshots…');

    const readAny = async (res) => {
      try {
        const j = await res.clone().json();
        return { kind: 'json', value: j };
      } catch {
        const t = await res.text().catch(() => '');
        return { kind: 'text', value: t };
      }
    };

    const snip = (v, n = 160) => {
      if (v == null) return '';
      const s = typeof v === 'string' ? v : JSON.stringify(v);
      return s.replace(/\s+/g, ' ').slice(0, n);
    };

    // 1) Capture snapshot per simulator (server-backed). If it fails, still proceed with a best-effort sinceMs.
    const snapshots = {};
    await Promise.all(
      simulatorWfmcs.map(async (wfmc) => {
        try {
          const res = await fetch(`/api/test/message/snapshot?wfmc=${encodeURIComponent(wfmc)}`, { cache: 'no-store' });
          const payload = await readAny(res);
          const snapshotResp = payload.kind === 'json' ? payload.value : String(payload.value);

          const v = payload.kind === 'json' ? payload.value : null;
          const ok = !!(res.ok && v && v.ok);
          const sinceMs = Number(v?.sinceMs ?? v?.snapshot?.sinceMs ?? v?.snapshot?.since_ms ?? null);
          const dt = String(v?.dt ?? v?.snapshot?.dt ?? v?.snapshot?.time ?? '');

          snapshots[wfmc] = {
            ok,
            wfmc,
            sinceMs: Number.isFinite(sinceMs) ? sinceMs : Date.now(),
            since: dt || new Date().toISOString(),
            snapshotResp
          };
        } catch (e) {
          snapshots[wfmc] = {
            ok: false,
            wfmc,
            sinceMs: Date.now(),
            since: new Date().toISOString(),
            snapshotResp: { error: e?.message || String(e) }
          };
        }
      })
    );

    setMsgResults(
      simulatorWfmcs.map((wfmc) => {
        const snap = snapshots[wfmc];
        const fallback = exampleLastMessages[wfmc] || null;
        return {
          wfmc,
          status: 'PENDING',
          details: snap?.ok ? 'Snapshot captured. Polling…' : 'Snapshot failed (best-effort). Polling…',
          ms: 0,
          snapshot: fallback || snap,
          influxStatus: 'PENDING',
          s3Status: 'PENDING',
          influxResp: null,
          s3Resp: null,
          snapshotResp: snap ? snap.snapshotResp : null,
          attempts: 0,
          lastCheckedMs: null
        };
      })
    );

    // 2) Poll persistence checks every 15s up to 300s. Stop early per WFMC when both Influx and S3 are found.
    const pollEveryMs = 15000;
    const maxTotalMs = 300000;
    const windowSec = 180;

    let pending = new Set(simulatorWfmcs);
    let intervalId = null;

    const runCheck = async (url) => {
      const res = await fetch(url, { cache: 'no-store' });
      const payload = await readAny(res);
      const respDbg = payload.kind === 'json' ? payload.value : String(payload.value);

      if (!res.ok) {
        return { ok: false, status: 'ERROR', resp: respDbg, details: `HTTP ${res.status}` };
      }
      if (payload.kind === 'json' && payload.value && (payload.value.ok === true || payload.value.ok === false)) {
        return {
          ok: payload.value.ok === true,
          status: payload.value.ok === true ? 'PASS' : 'FAIL',
          resp: respDbg,
          details: payload.value.reason ? String(payload.value.reason) : (payload.value.ok ? 'ok' : 'not_found')
        };
      }
      return { ok: false, status: 'ERROR', resp: respDbg, details: 'Invalid response shape' };
    };

    const tick = async () => {
      const now = Date.now();
      const elapsed = now - startedAt;

      if (elapsed >= maxTotalMs) {
        setMsgResults((prev) =>
          prev.map((r) => {
            if (r.status === 'PASS') return { ...r, ms: elapsed };
            return {
              ...r,
              status: 'FAIL',
              details: `Timeout after ${Math.round(elapsed / 1000)}s. ${r.details || ''}`.trim(),
              ms: elapsed
            };
          })
        );

        setMsgMsg(`Done. Timeout at ${Math.round(elapsed / 1000)}s. Marking remaining as FAIL.`);
        setMsgRunning(false);
        if (intervalId) window.clearInterval(intervalId);
        return;
      }

      const toCheck = Array.from(pending);
      if (toCheck.length === 0) {
        setMsgMsg(`Done. All persisted. (Elapsed ${Math.round(elapsed / 1000)}s)`);
        setMsgRunning(false);
        if (intervalId) window.clearInterval(intervalId);
        return;
      }

      let updates = [];
      try {
        updates = await Promise.all(
          toCheck.map(async (wfmc) => {
            const snap = snapshots[wfmc];
            const sinceMs = snap?.sinceMs || Date.now();

            const influxUrl = `/api/test/message/influx?wfmc=${encodeURIComponent(wfmc)}&sinceMs=${encodeURIComponent(String(sinceMs))}&windowSec=${encodeURIComponent(String(windowSec))}`;
            const s3Url = `/api/test/message/s3?wfmc=${encodeURIComponent(wfmc)}&sinceMs=${encodeURIComponent(String(sinceMs))}&windowSec=${encodeURIComponent(String(windowSec))}`;

            const [influx, s3] = await Promise.all([runCheck(influxUrl), runCheck(s3Url)]);
            const bothOk = influx.ok && s3.ok;

            const missingParts = [];
            if (!influx.ok) missingParts.push('Influx');
            if (!s3.ok) missingParts.push('S3');

            return {
              wfmc,
              bothOk,
              details: bothOk ? 'Found in Influx + S3' : `Waiting: ${missingParts.join(' & ')}`,
              influxStatus: influx.status,
              s3Status: s3.status,
              influxResp: influx.resp,
              s3Resp: s3.resp
            };
          })
        );
      } catch (e) {
        setMsgMsg(`Polling… transient error: ${snip(e?.message || String(e))}`);
        return;
      }

      updates.forEach((u) => {
        if (u.bothOk) pending.delete(u.wfmc);
      });

      setMsgResults((prev) =>
        prev.map((row) => {
          const u = updates.find((x) => x.wfmc === row.wfmc);
          if (!u) return row;
          const nextAttempts = (Number(row.attempts) || 0) + 1;
          return {
            ...row,
            status: u.bothOk ? 'PASS' : 'PENDING',
            details: u.details,
            ms: elapsed,
            attempts: nextAttempts,
            lastCheckedMs: now,
            influxStatus: u.influxStatus,
            s3Status: u.s3Status,
            influxResp: u.influxResp,
            s3Resp: u.s3Resp
          };
        })
      );

      const passSoFar = simulatorWfmcs.length - pending.size;
      setMsgMsg(`Polling… PASS ${passSoFar}/${simulatorWfmcs.length}. Elapsed ${Math.round(elapsed / 1000)}s.`);
    };

    setMsgMsg(`Captured snapshots. Polling every ${Math.round(pollEveryMs / 1000)}s up to ${Math.round(maxTotalMs / 1000)}s…`);
    await tick();
    intervalId = window.setInterval(() => {
      tick();
    }, pollEveryMs);
  };





  const runFirmwareTest = async () => {
    if (!fwEnabled) {
      setFwMsg('Enable the test to run it.');
      return;
    }

    if (!simulatorWfmcs.length) {
      setFwMsg('No simulator WFMCs to test.');
      return;
    }

    setFwRunning(true);
    setFwMsg('Running firmware checks…');

    const runOne = async (wfmc) => {
      const started = Date.now();
      try {
        const res = await fetch(`/api/test/firmware?wfmc=${encodeURIComponent(wfmc)}`, { cache: 'no-store' });
        const ms = Date.now() - started;

        // Try JSON first; if it fails, fall back to text so we can debug 404/HTML responses.
        let data = null;
        let text = '';
        try {
          data = await res.clone().json();
        } catch {
          try {
            text = await res.text();
          } catch {
            text = '';
          }
        }

        if (!res.ok || !data) {
          const snippet = text ? String(text).replace(/\s+/g, ' ').slice(0, 160) : '';
          console.warn('[fw-test] non-JSON or error response', { wfmc, status: res.status, text });
          return {
            wfmc,
            status: 'ERROR',
            details: `HTTP ${res.status}${snippet ? `: ${snippet}` : ''}`,
            ms
          };
        }

        if (data.ok) {
          const v = (data.actual && data.actual.settings && data.actual.settings.version) || '—';
          const f = (data.actual && data.actual.download && data.actual.download.fileName) || (data.expected && data.expected.fileName) || '—';
          return { wfmc, status: 'PASS', details: `settings v${v} · download ${f}`, ms };
        }

        const errs = Array.isArray(data.errors)
          ? data.errors.join(' | ')
          : (data.error || data.message || 'unknown error');
        return { wfmc, status: 'ERROR', details: errs, ms: data.ms ?? ms };
      } catch (e) {
        const ms = Date.now() - started;
        return { wfmc, status: 'ERROR', details: `Network error: ${e?.message || String(e)}`, ms };
      }
    };

    try {
      const results = await Promise.all(simulatorWfmcs.map(runOne));
      setFwResults(results);
      const okCount = results.filter(r => r.status === 'PASS').length;
      setFwMsg(`Done. ${okCount}/${results.length} passed.`);
    } finally {
      setFwRunning(false);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        System Services
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => window.open('/_dozzle/', '_blank')}
        >
          Dozzle Logs
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => window.open('https://influx-staging.terrra.one', '_blank')}
        >
          InfluxDB UI
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={() => loadServices()}
          sx={{ ml: 'auto' }}
          disabled={servicesLoading}
        >
          {servicesLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: 650 }}>
          System:
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          CPU: {Number.isFinite(systemCpuPct) ? `${systemCpuPct.toFixed(1)}%` : '—'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Memory: {Number.isFinite(systemMemPct) ? `${systemMemPct.toFixed(1)}%` : '—'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Message Rate: {Number.isFinite(messageRatePerMin) ? `${Math.round(messageRatePerMin)} per min` : '—'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Tests:{' '}
          {(() => {
            const fwTotal = fwResults.length;
            const fwPass = fwResults.filter(r => r.status === 'PASS').length;
            const settingsTotal = settingsResults.length;
            const settingsPass = settingsResults.filter(r => r.status === 'PASS').length;

            if (fwTotal === 0 && settingsTotal === 0) return '—';

            const allPass =
              (fwTotal === 0 || fwPass === fwTotal) &&
              (settingsTotal === 0 || settingsPass === settingsTotal);

            return (
              <span
                style={{
                  fontWeight: 650,
                  color: allPass ? '#2e7d32' : '#d32f2f'
                }}
              >
                {allPass ? 'PASS' : 'FAIL'}
              </span>
            );
          })()}
        </Typography>
      </Box>

      {/* Services table moved to the bottom to prevent layout jump on refresh */}


      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: 650 }}>
          Simulator Control
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={!!simPowerOn}
              onChange={(e) => setSimulatorPower(!!e.target.checked)}
              disabled={simScaling || simConfigLoading}
            />
          }
          label={simPowerOn ? 'ON' : 'OFF'}
        />

        <FormControlLabel
          control={
            <Switch
              checked={simMode === 'external'}
              onChange={(e) => setSimulatorMode(e.target.checked ? 'external' : 'internal')}
              disabled={simScaling || simConfigLoading || !simPowerOn}
            />
          }
          label={simMode === 'external' ? 'External' : 'Internal'}
        />

        <TextField
          size="small"
          type="number"
          label="Count"
          value={simCount}
          onChange={(e) => setSimCount(e.target.value)}
          inputProps={{ min: 0, max: 10, step: 1 }}
          sx={{ width: 120 }}
          disabled={simScaling || simConfigLoading || !simPowerOn}
        />

        <Button
          size="small"
          variant="contained"
          onClick={scaleSimulator}
          disabled={simScaling || simConfigLoading || !simPowerOn}
        >
          {simScaling ? 'Working…' : 'Apply'}
        </Button>

        {simMqttUrl ? (
          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
            Broker: <code>{simMqttUrl}</code>
          </Typography>
        ) : null}

        {simScaleMsg ? (
          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
            {simScaleMsg}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 650 }}>
          Firmware E2E Test (minimal)
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Checks <code>get_settings</code> version + firmware download filename for each simulator.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={fwEnabled}
                onChange={(e) => {
                  const enabled = !!e.target.checked;
                  setFwEnabled(enabled);
                  setFwResults([]);
                  setFwMsg(enabled ? 'Ready. Click “Run now”.' : 'Enable the test to see results.');
                }}
              />
            }
            label="Enable"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={compareBinaries}
                onChange={(e) => setCompareBinaries(!!e.target.checked)}
              />
            }
            label="Compare binaries"
          />
          <Button
            size="small"
            variant="contained"
            onClick={runFirmwareTest}
            disabled={!fwEnabled || fwRunning}
          >
            {fwRunning ? 'Running…' : 'Run now'}
          </Button>
          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
            {fwMsg}
          </Typography>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Simulator</TableCell>
              <TableCell>WFMC</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!fwEnabled) ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                  Enable the test to see results.
                </TableCell>
              </TableRow>
            ) : (fwResults.length === 0) ? (
              simulatorWfmcs.length ? (
                simulatorWfmcs.map((wfmc) => (
                  <TableRow key={wfmc}>
                    <TableCell>{wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                    <TableCell><code>{wfmc}</code></TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                    No simulator WFMCs detected.
                  </TableCell>
                </TableRow>
              )
            ) : (
              fwResults.map((r) => (
                <TableRow key={r.wfmc}>
                  <TableCell>{r.wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                  <TableCell><code>{r.wfmc}</code></TableCell>
                  <TableCell sx={{ fontWeight: 650, color: r.status === 'PASS' ? 'success.main' : 'error.main' }}>
                    {r.status}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.details}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {Number.isFinite(r.ms) ? `${r.ms}ms` : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 650 }}>
          Settings E2E Test (minimal)
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Validates <code>device?action=get_settings</code> response per simulator (including overrides).
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settingsEnabled}
                onChange={(e) => {
                  const enabled = !!e.target.checked;
                  setSettingsEnabled(enabled);
                  setSettingsResults([]);
                  setSettingsMsg(enabled ? 'Ready. Click “Run now”.' : 'Enable the test to see results.');
                }}
              />
            }
            label="Enable"
          />
          <Button
            size="small"
            variant="contained"
            onClick={runSettingsTest}
            disabled={!settingsEnabled || settingsRunning}
          >
            {settingsRunning ? 'Running…' : 'Run now'}
          </Button>
          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
            {settingsMsg}
          </Typography>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Simulator</TableCell>
              <TableCell>WFMC</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Freq (min)</TableCell>
              <TableCell>Details</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!settingsEnabled ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                  Enable the test to see results.
                </TableCell>
              </TableRow>
            ) : settingsResults.length === 0 ? (
              simulatorWfmcs.map((wfmc) => (
                <TableRow key={wfmc}>
                  <TableCell>{wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                  <TableCell><code>{wfmc}</code></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
                </TableRow>
              ))
            ) : (
              settingsResults.map((r) => (
                <TableRow key={r.wfmc}>
                  <TableCell>{r.wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                  <TableCell><code>{r.wfmc}</code></TableCell>
                  <TableCell sx={{ fontWeight: 650, color: r.status === 'PASS' ? 'success.main' : 'error.main' }}>
                    {r.status}
                  </TableCell>
                  <TableCell>{r.version ?? '—'}</TableCell>
                  <TableCell>{Number.isFinite(r.frqM) ? r.frqM : '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.details}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {Number.isFinite(r.ms) ? `${r.ms}ms` : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 650 }}>
          Message Persistence E2E Test
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Captures a snapshot, then polls every 15s (up to 300s) until the message is found in both InfluxDB and S3.
          Click a row to see debug details.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={msgEnabled}
                onChange={(e) => {
                  const enabled = !!e.target.checked;
                  setMsgEnabled(enabled);
                  setMsgResults([]);
                  setMsgMsg(enabled ? 'Ready. Click “Run now”.' : 'Enable the test to see results.');
                }}
              />
            }
            label="Enable"
          />

          <Button size="small" variant="contained" onClick={runMessageTest} disabled={!msgEnabled || msgRunning}>
            {msgRunning ? 'Running…' : 'Run now'}
          </Button>

          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
            {msgMsg}
          </Typography>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Simulator</TableCell>
              <TableCell>WFMC</TableCell>
              <TableCell>Influx</TableCell>
              <TableCell>S3</TableCell>
              <TableCell>Overall</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!msgEnabled ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>
                  Enable the test to see results.
                </TableCell>
              </TableRow>
            ) : msgResults.length === 0 ? (
              simulatorWfmcs.map((wfmc) => (
                <TableRow key={wfmc}>
                  <TableCell>{wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                  <TableCell><code>{wfmc}</code></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
                </TableRow>
              ))
            ) : (
              msgResults.map((r) => {
                const isOpen = openMsgWfmc === r.wfmc;

                const colorFor = (s) => {
                  if (s === 'PASS') return 'success.main';
                  if (s === 'FAIL') return 'error.main';
                  if (s === 'ERROR') return 'error.main';
                  if (s === 'WAITING' || s === 'PENDING') return 'warning.main';
                  return 'text.secondary';
                };

                const debugObj = {
                  wfmc: r.wfmc,
                  status: r.status,
                  details: r.details,
                  attempts: r.attempts,
                  lastCheckedMs: r.lastCheckedMs,
                  lastChecked: r.lastCheckedMs ? new Date(r.lastCheckedMs).toISOString() : null,
                  snapshotResp: r.snapshotResp ?? null,
                  influxResp: r.influxResp ?? null,
                  s3Resp: r.s3Resp ?? null
                };

                return (
                  <React.Fragment key={r.wfmc}>
                    <TableRow
                      hover
                      onClick={() => setOpenMsgWfmc(isOpen ? null : r.wfmc)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{r.wfmc.replace('SIMULATOR', 'simulator-')}</TableCell>
                      <TableCell><code>{r.wfmc}</code></TableCell>

                      <TableCell sx={{ fontWeight: 650, color: colorFor(r.influxStatus) }}>
                        {r.influxStatus ?? '—'}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 650, color: colorFor(r.s3Status) }}>
                        {r.s3Status ?? '—'}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 650, color: colorFor(r.status) }}>
                        {r.status}
                      </TableCell>

                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        {Number.isFinite(r.ms) ? `${r.ms}ms` : '—'}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              Debug details
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: '0.75rem',
                                lineHeight: 1.25,
                                color: 'text.secondary'
                              }}
                            >
                              {JSON.stringify(debugObj, null, 2)}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

    
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 650 }}>
          Services
        </Typography>

        <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Container</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell align="right">Restarts</TableCell>
                    <TableCell align="right">CPU %</TableCell>
                    <TableCell align="right">Mem %</TableCell>
                    <TableCell>Health</TableCell>
                  </TableRow>
                </TableHead>
        
                <TableBody>
                  {servicesError ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: 'error.main' }}>
                        {servicesError}
                      </TableCell>
                    </TableRow>
                  ) : servicesLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: 'text.secondary' }}>
                        Loading services…
                      </TableCell>
                    </TableRow>
                  ) : services.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: 'text.secondary' }}>
                        No services data yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    services.map((row) => {
                      const isOpen = openServiceKey === row.key;
                      const started = row.startedAt ? new Date(row.startedAt).toLocaleString() : '—';
                      const cpu = Number.isFinite(row.cpuPercent) ? `${row.cpuPercent.toFixed(1)}%` : '—';
                      const mem = Number.isFinite(row.memPercent) ? `${row.memPercent.toFixed(1)}%` : '—';
                      const healthText = row.healthOk ? 'OK' : 'ERROR';
        
                      return (
                        <React.Fragment key={row.key}>
                          <TableRow
                            hover
                            onClick={() => setOpenServiceKey(isOpen ? null : row.key)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ fontWeight: 650 }}>{row.displayName || row.service}</TableCell>
                            <TableCell>
                              <code>{row.containerName || '—'}</code>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: row.running ? 'success.main' : 'text.secondary' }}>
                              {row.running ? 'RUNNING' : (row.state ? String(row.state).toUpperCase() : 'STOPPED')}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{started}</TableCell>
                            <TableCell align="right">{row.restartCount}</TableCell>
                            <TableCell align="right">{cpu}</TableCell>
                            <TableCell align="right">{mem}</TableCell>
                            <TableCell sx={{ fontWeight: 650, color: row.healthOk ? 'success.main' : 'error.main' }}>
                              {healthText}
                            </TableCell>
                          </TableRow>
        
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0 }}>
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 1.5 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Health details
                                  </Typography>
        
                                  {/* Show the (potentially long) health message only when expanded */}
                                  {row.healthDetails && row.healthDetails.message ? (
                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                      {String(row.healthDetails.message)}
                                    </Typography>
                                  ) : null}
        
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      fontSize: '0.75rem',
                                      lineHeight: 1.25,
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {JSON.stringify(row.healthDetails, null, 2)}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
      </Box>
</Card>
  );
}
